import { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { Button } from 'ui';
import { Icon } from 'ui/Icon';
import { OpenStatus } from '../utils/openStatus';

export interface SectionFilter<T extends string> {
  key: T;
  label: string;
}

interface SectionToolbarProps<T extends string> {
  title: string;
  count: number;
  filters?: SectionFilter<T>[];
  activeFilter?: T;
  onFilterChange?: (key: T) => void;
}

export const SectionToolbar = <T extends string>({
  title,
  count,
  filters = [],
  activeFilter,
  onFilterChange,
}: SectionToolbarProps<T>) => (
  <div className={'farm-section-toolbar'}>
    <div className={'farm-section-heading'}>
      <h2 className={'farm-section-title'}>{title}</h2>
      <span className={'farm-section-count'}>{count}</span>
    </div>
    {filters.length > 1 && (
      <div className={'farm-section-filters'}>
        {filters.map((filter) => (
          <Button
            key={filter.key}
            className={`farm-section-filter${filter.key === activeFilter ? ' active' : ''}`}
            label={filter.label}
            onClick={() => onFilterChange?.(filter.key)}
          />
        ))}
      </div>
    )}
  </div>
);

export const SectionIcon = ({ name }: { name: string }) => (
  <span className={'farm-section-icon'}>
    <Icon name={name} type={'any'} />
  </span>
);

export const RowIcon = ({ name }: { name: string }) => (
  <span className={'farm-row-icon'}>
    <Icon name={name} type={'any'} />
  </span>
);

interface ViewButtonProps {
  active: boolean;
  onClick: () => void;
}

export const ViewButton = ({ active, onClick }: ViewButtonProps) => {
  const intl = useIntl();

  return (
    <Button
      className={`farm-view-btn${active ? ' active' : ''}`}
      label={intl.formatMessage({ id: 'farm_view.view' })}
      icon={
        <span className={'farm-view-btn-icon'}>
          <Icon
            name={active ? 'farm-arrow-right-light' : 'farm-arrow-right'}
            type={'any'}
          />
        </span>
      }
      iconPosition={'end'}
      onClick={onClick}
    />
  );
};

export const SectionNote = ({ children }: { children: ReactNode }) => (
  <div className={'farm-section-note'}>
    <span className={'farm-section-note-icon'}>
      <Icon name={'farm-info'} type={'any'} />
    </span>
    <span>{children}</span>
  </div>
);

const STATUS_ICON = {
  open: 'farm-status-open',
  closing: 'farm-status-closing',
  closed: 'farm-status-closed',
};

export const StatusPill = ({ status }: { status: OpenStatus }) => {
  const intl = useIntl();

  const label = (() => {
    if (status.state === 'open') {
      return intl.formatMessage(
        { id: 'farm_view.status_open' },
        { time: status.closesAt }
      );
    }

    if (status.state === 'closing') {
      return intl.formatMessage(
        { id: 'farm_view.status_closing' },
        { minutes: status.minutesLeft }
      );
    }

    if (!status.opensAt) {
      return intl.formatMessage({ id: 'open_hours.closed' });
    }

    return status.dayOffset === 0
      ? intl.formatMessage(
          { id: 'farm_view.status_closed_today' },
          { time: status.opensAt }
        )
      : intl.formatMessage(
          { id: 'farm_view.status_closed' },
          {
            day: intl.formatMessage({
              id: `open_hours.short.${status.dayKey}`,
            }),
            time: status.opensAt,
          }
        );
  })();

  return (
    <span className={`farm-status farm-status-${status.state}`}>
      <span className={'farm-status-dot'}>
        <Icon name={STATUS_ICON[status.state]} type={'any'} />
      </span>
      {label}
    </span>
  );
};
